import { z } from 'zod'
import { STAGE_IDS, CHANNELS, BUCKET_FIELDS } from '@joker/domain'

/**
 * Zod is the single source of truth: it validates input, produces the
 * TypeScript types the client and server share, and generates the OpenAPI
 * document for /api/v1. One definition, three uses, no drift.
 */

export const stageIdSchema = z.enum(STAGE_IDS)
export const channelSchema = z.enum(CHANNELS)

export const bucketRuleSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.object({
      field: z.enum(BUCKET_FIELDS),
      op: z.enum(['eq', 'neq']),
      value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
    }),
    z.object({
      field: z.enum(BUCKET_FIELDS),
      op: z.enum(['gt', 'gte', 'lt', 'lte']),
      value: z.number(),
    }),
    z.object({
      field: z.enum(BUCKET_FIELDS),
      op: z.enum(['in', 'nin']),
      value: z.array(z.union([z.string(), z.number()])),
    }),
    z.object({ field: z.enum(BUCKET_FIELDS), op: z.enum(['isNull', 'notNull']) }),
    z.object({
      order: z
        .object({
          products: z.array(z.string().min(1)).optional(),
          minCents: z.number().int().nonnegative().optional(),
          maxCents: z.number().int().nonnegative().optional(),
          paidFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
          paidBefore: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
          codes: z.array(z.string().min(1)).optional(),
        })
        .strict(),
    }),
    z.object({ all: z.array(bucketRuleSchema) }),
    z.object({ any: z.array(bucketRuleSchema) }),
    z.object({ not: bucketRuleSchema }),
  ]),
)

/* ----------------------------- pipeline ----------------------------- */

export const stageCountSchema = z.object({
  stage: stageIdSchema,
  label: z.string(),
  definition: z.string(),
  severity: z.enum(['critical', 'serious', 'warning', 'good']).nullable(),
  count: z.number().int(),
  deltaWeek: z.number().int(),
})
export type StageCount = z.infer<typeof stageCountSchema>

/** One step of the journey, with how many reached it and how many stopped there. */
export const funnelStepSchema = z.object({
  id: z.string(),
  label: z.string(),
  hint: z.string(),
  count: z.number().int(),
  /** Share of the step before it. Null on the first step, which has none. */
  ofPrevious: z.number().nullable(),
})

export const pipelineSummarySchema = z.object({
  totalPeople: z.number().int(),
  paidPeople: z.number().int(),
  trainingThisWeek: z.number().int(),
  medianDaysPaidToFirstSession: z.number().nullable(),
  stages: z.array(stageCountSchema),

  /** Leads are people with no Oryx account: they may still have paid. */
  leadsWaiting: z.number().int(),
  leadsPaid: z.number().int(),
  /** Of the leads, how many came from the Klaviyo waitlist rather than a form. */
  leadsFromWaitlist: z.number().int(),
  /** Everyone who has ever trained, and everyone who finished their program. */
  everTrained: z.number().int(),
  completed: z.number().int(),
  /** Trained at least once and has a program to finish — the denominator for
      "finished", since somebody with no program can never reach the end of one. */
  trainedWithProgram: z.number().int(),
  /** Patients holding one of our tablets. */
  tabletsOut: z.number().int(),
  vips: z.number().int(),
  /** Median days from joining the waitlist to paying. */
  medianDaysLeadToPaid: z.number().nullable(),

  funnel: z.array(funnelStepSchema),

  /** The optometrist channel, kept apart from the patient funnel entirely. */
  optometrist: z.object({
    enquiries: z.number().int(),
    practices: z.number().int(),
    patients: z.number().int(),
    paying: z.number().int(),
    revenueUsdCents: z.number().int(),
    byPractice: z.array(
      z.object({
        name: z.string(),
        locationName: z.string().nullable(),
        patients: z.number().int(),
        paying: z.number().int(),
        training: z.number().int(),
        revenueUsdCents: z.number().int(),
      }),
    ),
  }),

  money: z.object({
    revenueUsdCents: z.number().int(),
    thisMonthUsdCents: z.number().int(),
    lastMonthUsdCents: z.number().int(),
    bySource: z.array(
      z.object({ source: z.string(), usdCents: z.number().int(), people: z.number().int() }),
    ),
  }),
})
export type PipelineSummary = z.infer<typeof pipelineSummarySchema>

/* ------------------------------ cohorts ----------------------------- */

/** 'saved': each cohort made on the Buckets screen, against everybody who paid. */
export const cohortSplitSchema = z.enum(['channel', 'practice', 'month', 'rx', 'age', 'saved'])
export type CohortSplit = z.infer<typeof cohortSplitSchema>

export const cohortRowSchema = z.object({
  key: z.string(),
  n: z.number().int(),
  milestones: z.array(z.object({ sessions: z.number().int(), count: z.number().int(), pct: z.number() })),
})

export const cohortMatrixSchema = z.object({
  split: cohortSplitSchema,
  paidInScope: z.number().int(),
  rows: z.array(cohortRowSchema),
})
export type CohortMatrix = z.infer<typeof cohortMatrixSchema>

/* ----------------------------- retention ---------------------------- */

export const retentionSeriesSchema = z.object({
  key: z.string(),
  label: z.string(),
  /** index = day since purchase; null where the denominator is too small to report */
  points: z.array(z.number().nullable()),
})

export const retentionSchema = z.object({
  maxDay: z.number().int(),
  minDenominator: z.number().int(),
  series: z.array(retentionSeriesSchema),
})
export type Retention = z.infer<typeof retentionSchema>

/* --------------------------- optometrists ---------------------------- */

export const optometristUserTypeSchema = z.enum(['test', 'team', 'paying'])
export type OptometristUserType = z.infer<typeof optometristUserTypeSchema>

export const OPTOMETRIST_USER_TYPE_LABELS: Record<OptometristUserType, string> = {
  test: 'Store demo',
  team: 'Team member',
  paying: 'Paying patient',
}

/** The editable half of a practice — everything a human maintains by hand. */
export const practiceProfileSchema = z.object({
  /** The business: "Classic Vision Care". Falls back to the Oryx name if blank. */
  practiceName: z.string().trim().max(200).nullable(),
  /** The branch: "East Cobb". Blank for a single-site practice. */
  locationName: z.string().trim().max(200).nullable(),
  /** Accepts a bare domain as readily as a full URL; not validated as a URL,
      because rejecting "classicvisioncare.com" would be the wrong lesson. */
  website: z.string().trim().max(300).nullable(),
  optometristName: z.string().trim().max(200).nullable(),
  optometristIsDoctor: z.boolean(),
  optometristEmail: z.string().trim().email().nullable().or(z.literal('').transform(() => null)),
  optometristPhone: z.string().trim().max(60).nullable(),
  contactName: z.string().trim().max(200).nullable(),
  contactEmail: z.string().trim().email().nullable().or(z.literal('').transform(() => null)),
  contactPhone: z.string().trim().max(60).nullable(),
  country: z.string().trim().max(80).nullable(),
  address: z.string().trim().max(300).nullable(),
  city: z.string().trim().max(120).nullable(),
  /** ZIP in the US, postal code elsewhere. */
  postalCode: z.string().trim().max(20).nullable(),
  /** Only meaningful in the US; null everywhere else rather than invented. */
  state: z.string().trim().max(60).nullable(),
  currency: z.string().trim().length(3).toUpperCase(),
  /** Minor units, like every other amount here. */
  patientPriceCents: z.number().int().min(0).nullable(),
  ourCutCents: z.number().int().min(0).nullable(),
})
export type PracticeProfile = z.infer<typeof practiceProfileSchema>

export const optometristSchema = practiceProfileSchema.extend({
  id: z.string(),
  name: z.string(),
  people: z.number().int(),
  training: z.number().int(),
  completed: z.number().int(),
  sessions: z.number().int(),
  lastSessionAt: z.string().datetime().nullable(),
  /** Headcount by what the patient is to the practice. */
  paying: z.number().int(),
  team: z.number().int(),
  test: z.number().int(),
  unclassified: z.number().int(),
  /** paying x our cut, in this practice's own currency. Never cross-summed. */
  revenueCents: z.number().int(),
  /** The same revenue in dollars, each patient converted on their own day.
      This one can be totalled across practices; revenueCents cannot. */
  revenueUsdCents: z.number().int(),
})
export type Optometrist = z.infer<typeof optometristSchema>

export const practicePatientSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  oryxUserNumber: z.number().int().nullable(),
  sessionsTotal: z.number().int(),
  /** What the old Cortex dashboard reports, where Joker has no rows of its own. */
  reportedSessions: z.number().int().nullable(),
  userType: optometristUserTypeSchema.nullable(),
})
export type PracticePatient = z.infer<typeof practicePatientSchema>

export const ageBandPersonSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  age: z.number().int().nullable(),
  sessionsTotal: z.number().int(),
  practiceName: z.string().nullable(),
  stage: stageIdSchema,
})
export type AgeBandPerson = z.infer<typeof ageBandPersonSchema>

/* --------------------------- prospective users ----------------------- */

export const prospectSchema = z.object({
  id: z.string(),
  code: z.string().nullable(),
  name: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  location: z.string().nullable(),
  source: z.string().nullable(),
  note: z.string().nullable(),
  /** How they described themselves on the form. */
  whoIAm: z.string().nullable(),
  /** What they wrote on the form, in their words. */
  message: z.string().nullable(),
  joinedAt: z.string().datetime().nullable(),
  /** False once they became a patient; the record and the id stay the same. */
  waiting: z.boolean(),
  /** Has actually paid — not merely present in Joker as a patient. */
  paid: z.boolean(),
  /** What they paid, in minor units, and in which currency. */
  pricePaidCents: z.number().int().nullable(),
  /** The patient they bought the programme for, when the buyer is not the trainee. */
  /** A would-be patient, or an optometrist enquiring about the programme. */
  leadKind: z.enum(['patient', 'optometrist']),
  purchasedForId: z.string().nullable(),
  purchasedForName: z.string().nullable(),
  currency: z.string().nullable(),
  /** How the money arrived, from the order's source. */
  paymentMethod: z.string().nullable(),
  /** The same payment in dollars, at the rate on the day it was taken. */
  pricePaidUsdCents: z.number().int().nullable(),
  /** Above one, no single native amount is true and only the dollar total is. */
  paidCurrencyCount: z.number().int(),
  /** When their patient record was created — the stand-in purchase date. */
  recordCreatedAt: z.string().datetime().nullable(),
  /** Set on a converted prospect: when they paid, and what they are now. */
  convertedAt: z.string().datetime().nullable(),
  stage: z.string().nullable(),
  sessionsTotal: z.number().int(),
  practiceName: z.string().nullable(),
  /** Marked a test signup by hand; listed but excluded from every count. */
  isTest: z.boolean(),
  /** A prescription file (PDF, JPG or PNG) has been uploaded. The file itself is
      fetched separately, from /api/prospects/:id/prescription. */
  hasPrescription: z.boolean(),
  prescriptionFilename: z.string().nullable(),
  prescriptionUploadedAt: z.string().datetime().nullable(),
  /** The verdict on their prescription. "not_reviewed" until somebody decides. */
  prescriptionReview: z.lazy(() => prescriptionReviewSchema),
  prescriptionReviewedAt: z.string().datetime().nullable(),
  prescriptionReviewedBy: z.string().nullable(),
  /** The grade of the newest scanned prescription, a hand override first. Null until one is graded. */
  eligibility: z.enum(['suitable', 'not_suitable', 'refer', 'needs_answers', 'incomplete']).nullable(),
  eligibilityIsOverride: z.boolean(),
  prescriptionCount: z.number().int(),
  /** Their Wix order numbers, oldest first, as on the receipts ("10071"). */
  wixOrderNumbers: z.array(z.string()),
  /** Where their latest order with an address was sent, on one line. */
  mailingAddress: z.string().nullable(),
  /** What Klaviyo sent them: emails received, and how many different ones they opened and clicked. */
  emailsSent: z.number().int(),
  emailsOpened: z.number().int(),
  emailsClicked: z.number().int(),
  emailUnsubscribed: z.boolean(),
  /** The newest email they received, and when. */
  lastEmailSubject: z.string().nullable(),
  lastEmailAt: z.string().datetime().nullable(),
  /** What they answered on the waitlist form. */
  ageRange: z.string().nullable(),
  /** "Do you have an eye prescription from the past 6 months?" */
  hasRecentPrescription: z.boolean().nullable(),
  outsideUs: z.boolean().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
  /** The waitlist page they signed up on: regular, timferriss, Wix… */
  campaign: z.string().nullable(),
  promo: z.string().nullable(),
})
export type Prospect = z.infer<typeof prospectSchema>

/* ------------------------------ orders ------------------------------ */

export const orderPaymentSchema = z.enum(['paid', 'refunded', 'partially_refunded', 'pending'])
export const orderFulfillmentSchema = z.enum(['fulfilled', 'unfulfilled', 'partially_fulfilled', 'canceled'])
export type OrderPayment = z.infer<typeof orderPaymentSchema>
export type OrderFulfillment = z.infer<typeof orderFulfillmentSchema>
export const ORDER_PAYMENT_LABELS: Record<OrderPayment, string> = {
  paid: 'Paid',
  refunded: 'Refunded',
  partially_refunded: 'Partly refunded',
  pending: 'Pending',
}
export const ORDER_FULFILLMENT_LABELS: Record<OrderFulfillment, string> = {
  fulfilled: 'Fulfilled',
  unfulfilled: 'Unfulfilled',
  partially_fulfilled: 'Partly fulfilled',
  canceled: 'Canceled',
}

/** One order, as a row of the Purchases list. */
export const orderRowSchema = z.object({
  id: z.string(),
  /** The store's number ("10074"); null for a payment recorded by hand. */
  number: z.string().nullable(),
  /** wix, wire, quickbooks, optometrist… */
  source: z.string(),
  storeName: z.string().nullable(),
  at: z.string().datetime(),
  customerId: z.string(),
  customerName: z.string().nullable(),
  customerEmail: z.string().nullable(),
  /** The customer's lead id (PR-0111) when they came through the waitlist or the form. */
  leadCode: z.string().nullable(),
  payment: orderPaymentSchema,
  /** Null when the store has no notion of it, as for a wire transfer. */
  fulfillment: orderFulfillmentSchema.nullable(),
  totalCents: z.number().int(),
  currency: z.string(),
  /** The total in dollars at the day's rate, gross; null when there is no rate. */
  usdCents: z.number().int().nullable(),
  /** The same net of VAT — what we keep, and what every revenue figure adds up. */
  netUsdCents: z.number().int().nullable(),
  itemCount: z.number().int(),
  /** A team test: marked so by hand, a Wix order of $1 or less, or a test customer. */
  isTest: z.boolean(),
  /** Set by hand: true = a test, false = a real sale; null = the automatic rule decides. */
  testMarked: z.boolean().nullable(),
  /** Why it counts as a test when nobody marked it: 'small' ($1 or less), 'customer' (a test account). */
  testReason: z.enum(['marked', 'small', 'customer']).nullable(),
  /** The customer's prescription, as on the Leads screen: on file, graded, reviewed. */
  hasPrescription: z.boolean(),
  eligibility: z.enum(['suitable', 'not_suitable', 'refer', 'needs_answers', 'incomplete']).nullable(),
  eligibilityIsOverride: z.boolean(),
  prescriptionReview: z.lazy(() => prescriptionReviewSchema),
  /** After the sale: whether the person training has an Oryx account and has started. */
  onboarding: z.object({
    /** The buyer, or the person they bought it for. */
    traineeId: z.string(),
    /** Set only when the trainee is somebody other than the buyer. */
    traineeName: z.string().nullable(),
    hasAccount: z.boolean(),
    /** Cortex's status for the account; "Demo" means made, details not filled in yet. */
    accountStatus: z.string().nullable(),
    sessions: z.number().int(),
    firstSessionAt: z.string().datetime().nullable(),
    lastSessionAt: z.string().datetime().nullable(),
  }),
  /** The latest shipment's tracking, when there is one. */
  tracking: z
    .object({
      carrier: z.string().nullable(),
      number: z.string().nullable(),
      link: z.string().nullable(),
      status: z.string(),
      statusAt: z.string().datetime().nullable(),
    })
    .nullable()
    .default(null),
})
export type OrderRow = z.infer<typeof orderRowSchema>

/** One shipment for an order, and every status it has passed through. */
export const shipmentSchema = z.object({
  id: z.string(),
  carrier: z.string().nullable(),
  trackingNumber: z.string().nullable(),
  trackingLink: z.string().nullable(),
  fulfilledAt: z.string().datetime().nullable(),
  status: z.string(),
  statusAt: z.string().datetime().nullable(),
  events: z.array(
    z.object({ status: z.string(), at: z.string().datetime(), detail: z.string().nullable(), source: z.string() }),
  ),
})
export type Shipment = z.infer<typeof shipmentSchema>

export const orderDetailSchema = orderRowSchema.extend({
  lineItems: z.array(
    z.object({ name: z.string(), quantity: z.number(), priceCents: z.number().int(), totalCents: z.number().int() }),
  ),
  priceSummary: z
    .object({
      subtotalCents: z.number().int(),
      shippingCents: z.number().int(),
      taxCents: z.number().int(),
      discountCents: z.number().int(),
      totalCents: z.number().int(),
      refundedCents: z.number().int(),
    })
    .nullable(),
  shippingTitle: z.string().nullable(),
  activities: z.array(z.object({ type: z.string(), at: z.string().nullable() })),
  mailingAddress: z.string().nullable(),
  customerPhone: z.string().nullable(),
  promoCode: z.string().nullable(),
  refundedAt: z.string().datetime().nullable(),
  /** What Joker counts: net of VAT, in dollars. Null for a refund or a test. */
  netUsdCents: z.number().int().nullable(),
  fxRate: z.number().nullable(),
  fxRateOn: z.string().nullable(),
  vatRate: z.number().nullable(),
  /** The store's raw words, for whoever needs to check them against Wix. */
  storePaymentStatus: z.string().nullable(),
  storeOrderStatus: z.string().nullable(),
  storeFulfillmentStatus: z.string().nullable(),
  shipments: z.array(z.lazy(() => shipmentSchema)).default([]),
})
export type OrderDetail = z.infer<typeof orderDetailSchema>

/* -------------------------- abandoned carts -------------------------- */

/** A checkout somebody started on the store and did not pay. */
export const abandonedCartSchema = z.object({
  id: z.string(),
  storeName: z.string().nullable(),
  abandonedAt: z.string().datetime(),
  email: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  phone: z.string().nullable(),
  otherPhones: z.array(z.string()),
  /** "Del Mar, CA 92014, US" — from the store's contact record, when it has one. */
  address: z.string().nullable(),
  products: z.string(),
  quantity: z.number().int(),
  listPriceCents: z.number().int(),
  totalCents: z.number().int(),
  currency: z.string().nullable(),
  discountCodes: z.array(z.string()),
  remindersSent: z.number().int(),
  lastReminderAt: z.string().datetime().nullable(),
  recoveryUrl: z.string().nullable(),
  language: z.string().nullable(),
  contactCreatedAt: z.string().datetime().nullable(),
  /** The person Joker already knows with this email; no person is ever made for a cart. */
  personId: z.string().nullable(),
  leadCode: z.string().nullable(),
  /** Paid for something after abandoning this cart, or before it. */
  boughtAfter: z.boolean(),
  boughtBefore: z.boolean(),
  /** Wix's own state: ABANDONED, or RECOVERED when they paid through the cart. */
  wixStatus: z.string().nullable(),
  /** The first purchase after the cart was left — the sale it turned into. */
  afterOrder: z
    .object({
      id: z.string(),
      number: z.string().nullable(),
      at: z.string().datetime(),
      amountCents: z.number().int(),
      currency: z.string().nullable(),
    })
    .nullable(),
  /** Our own: a team address, or a test or staff account. */
  isTest: z.boolean(),
})
export type AbandonedCart = z.infer<typeof abandonedCartSchema>

/* ----------------------------- programs ----------------------------- */

/** One run of training on one Oryx or Cortex account, owned by one human. */
export const programSchema = z.object({
  /** The account's own record id; /programs/:id. */
  id: z.string(),
  personId: z.string(),
  personName: z.string().nullable(),
  source: z.enum(['cortex', 'oryx_app']),
  oryxUserNumber: z.number().int().nullable(),
  /** The name on the account, which carries the operator's note: "(OLD)", "(without glasses)". */
  accountName: z.string().nullable(),
  /** The programme followed, and how many sessions it has. */
  programClass: z.string().nullable(),
  programSessions: z.number().int().nullable(),
  sessionsTotal: z.number().int(),
  firstSessionAt: z.string().datetime().nullable(),
  lastSessionAt: z.string().datetime().nullable(),
  stage: z.string(),
  /** The person's own account, rather than one merged into them. */
  isPrimary: z.boolean(),
})
export type Program = z.infer<typeof programSchema>

export const PRESCRIPTION_REVIEW_STATUSES = [
  'not_reviewed',
  'approved',
  'need_research',
  'out_of_criteria',
  'declined',
] as const
export const prescriptionReviewSchema = z.enum(PRESCRIPTION_REVIEW_STATUSES)

/** One note in the thread about a lead's prescription. */
export const prescriptionCommentSchema = z.object({
  id: z.string(),
  body: z.string(),
  author: z.string().nullable(),
  at: z.string().datetime(),
})
export type PrescriptionComment = z.infer<typeof prescriptionCommentSchema>

/** One change to a lead's prescription: a new approval status, or a file added, replaced or removed. */
export const prescriptionHistoryEventSchema = z.object({
  kind: z.enum(['review', 'upload', 'remove']),
  at: z.string().datetime(),
  /** Who made the change. */
  by: z.string().nullable(),
  /** Approval changes: the status before (null when it was not recorded) and after. */
  from: prescriptionReviewSchema.nullable(),
  to: prescriptionReviewSchema.nullable(),
  /** File changes: the file, and on an upload the one it replaced. */
  filename: z.string().nullable(),
  replaced: z.string().nullable(),
})
export type PrescriptionHistoryEvent = z.infer<typeof prescriptionHistoryEventSchema>
export type PrescriptionReview = z.infer<typeof prescriptionReviewSchema>
export const PRESCRIPTION_REVIEW_LABELS: Record<PrescriptionReview, string> = {
  not_reviewed: 'Not reviewed',
  approved: 'Approved',
  need_research: 'Need research',
  out_of_criteria: 'Out of criteria',
  declined: 'Declined',
}

/** What Klaviyo sent a lead, and what they did with it. */
export const emailEventSchema = z.object({
  kind: z.string(),
  subject: z.string().nullable(),
  at: z.string().datetime(),
})
export type EmailEvent = z.infer<typeof emailEventSchema>

/** The currencies anybody actually pays in. */
export const PAYMENT_CURRENCIES = ['USD', 'ILS', 'CHF'] as const
export const paymentCurrencySchema = z.enum(PAYMENT_CURRENCIES)
export type PaymentCurrency = z.infer<typeof paymentCurrencySchema>

/** NIS is what people call it; ILS is what the standard calls it. */
export const CURRENCY_LABELS: Record<PaymentCurrency, string> = {
  USD: 'USD $',
  ILS: 'NIS ₪',
  CHF: 'CHF',
}

/**
 * How the money reached us. Stored as the order's `source`, which is the same
 * column the Wix sync writes, so a hand-recorded payment and a synced one are
 * the same kind of thing and every revenue figure sees both.
 */
export const PAYMENT_METHODS = ['optometrist', 'wix', 'shopify', 'quickbooks', 'wire'] as const
export const paymentMethodSchema = z.enum(PAYMENT_METHODS)
export type PaymentMethod = z.infer<typeof paymentMethodSchema>

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  optometrist: 'Optometrist',
  wix: 'Wix store',
  shopify: 'Shopify',
  quickbooks: 'QuickBooks',
  wire: 'Bank transfer',
}

export const recordPaymentSchema = z.object({
  personId: z.string().uuid(),
  /** Major units as typed — 1000, not 100000. Converted on the way in. */
  amount: z.number().min(0),
  currency: paymentCurrencySchema,
  method: paymentMethodSchema,
  paidOn: z.string(),
})
export type RecordPayment = z.infer<typeof recordPaymentSchema>

/** The editable half of a prospective user. Blank clears the field. */
export const prospectEditSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string().trim().max(120).nullable(),
  lastName: z.string().trim().max(120).nullable(),
  email: z.string().trim().max(200).nullable(),
  phone: z.string().trim().max(60).nullable(),
  source: z.string().trim().max(200).nullable(),
  location: z.string().trim().max(200).nullable(),
  whoIAm: z.string().trim().max(500).nullable(),
  message: z.string().trim().max(4000).nullable(),
  note: z.string().trim().max(4000).nullable(),
  joinedAt: z.string().nullable(),
})
export type ProspectEdit = z.infer<typeof prospectEditSchema>

/* ------------------------------- usage ------------------------------- */

export const usageSummarySchema = z.object({
  sessions: z.number().int(),
  sessionPatients: z.number().int(),
  blocks: z.number().int(),
  /** Sessions that have block results, so `blocks` can be read as coverage. */
  sessionsWithBlocks: z.number().int(),
  etests: z.number().int(),
  etestPatients: z.number().int(),
  medianSessionMinutes: z.number().nullable(),
  /** Sessions per week over the last eight weeks, newest last. */
  weekly: z.array(z.object({ week: z.string(), sessions: z.number().int(), people: z.number().int() })),
  /** How the exercise blocks break down by name. */
  blockKinds: z.array(
    z.object({
      name: z.string(),
      blocks: z.number().int(),
      medianThreshold: z.number().nullable(),
      accuracy: z.number().nullable(),
    }),
  ),
  /** Acuity measured by the e-tests, bucketed so the spread is visible. */
  acuity: z.array(z.object({ label: z.string(), count: z.number().int() })),
  appVersions: z.array(z.object({ version: z.string(), sessions: z.number().int() })),
  /**
   * Where the training population sits in the programme. One row per segment,
   * counting only people who have actually trained — an eye test on its own is
   * not a position in the programme.
   */
  segments: z.array(
    z.object({
      /** -1 stands for "no segment recorded"; 0 is Oryx's own unsegmented seq. */
      seq: z.number().int(),
      label: z.string(),
      /** Patients whose most recent session was in this segment. */
      current: z.number().int(),
      /** Patients who have ever trained in it, whether or not they are still there. */
      everReached: z.number().int(),
      sessions: z.number().int(),
    }),
  ),
  /** Patients with at least one training session; the denominator for segments. */
  trainingPatients: z.number().int(),
  /**
   * How many of the people who have ever trained are still training, at
   * narrowing windows. Every row is a subset of the one above it, and the
   * percentage is always of the all-time figure, so the drop down the list is
   * the shape of attrition rather than a set of unrelated ratios.
   */
  recency: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      count: z.number().int(),
      pct: z.number(),
      /** False for a row that is not a subset of the one above it — "yesterday"
          is a day of its own, not a narrower window than "today". */
      nested: z.boolean(),
    }),
  ),
  /** The same windows over paying patients only. Its percentages are of the
      paying population, so the two lists are each internally consistent. */
  recencyPaid: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      count: z.number().int(),
      pct: z.number(),
      nested: z.boolean(),
    }),
  ),
  /** Everyone who has trained, so the tab answers "who" as well as "how much". */
  patients: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      sessions: z.number().int(),
      blocks: z.number().int(),
      etests: z.number().int(),
      firstSessionAt: z.string().nullable(),
      lastSessionAt: z.string().nullable(),
      medianMinutes: z.number().nullable(),
      firstAcuity: z.number().nullable(),
      lastAcuity: z.number().nullable(),
    }),
  ),
})
export type UsageSummary = z.infer<typeof usageSummarySchema>

/** One training session with the exercise blocks it ran. */
export const personSessionsSchema = z.object({
  sessions: z.array(
    z.object({
      id: z.string(),
      sourceId: z.string(),
      number: z.number().int().nullable(),
      startedAt: z.string(),
      endedAt: z.string().nullable(),
      durationSeconds: z.number().int().nullable(),
      appVersion: z.string().nullable(),
      trainingStage: z.number().int().nullable(),
      segmentSeq: z.number().int().nullable(),
      segmentSession: z.number().int().nullable(),
      completed: z.boolean(),
      hasIdles: z.boolean().nullable(),
      overriden: z.boolean().nullable(),
      blocks: z.array(
        z.object({
          blockId: z.number().int(),
          name: z.string().nullable(),
          rightAnswers: z.number().int().nullable(),
          wrongAnswers: z.number().int().nullable(),
          trials: z.number().int().nullable(),
          reversals: z.number().int().nullable(),
          threshold: z.number().nullable(),
          thresholdScore: z.number().nullable(),
          trend: z.string().nullable(),
          endReason: z.string().nullable(),
          contrast: z.number().nullable(),
          size: z.number().int().nullable(),
          orientation: z.number().int().nullable(),
          seconds: z.number().int().nullable(),
        }),
      ),
    }),
  ),
  etests: z.array(
    z.object({
      id: z.string(),
      takenAt: z.string().nullable(),
      afterSessionNumber: z.number().int().nullable(),
      acuity: z.number().nullable(),
      threshold: z.number().nullable(),
      rightAnswers: z.number().int().nullable(),
      wrongAnswers: z.number().int().nullable(),
      reactionMs: z.number().nullable(),
      sittingDistance: z.number().int().nullable(),
      seconds: z.number().int().nullable(),
    }),
  ),
})
export type PersonSessions = z.infer<typeof personSessionsSchema>

/* ------------------------------ revenue ------------------------------ */

export const revenuePointSchema = z.object({
  /** 'YYYY-MM' for a month, 'YYYY' for a year. */
  period: z.string(),
  usdCents: z.number().int(),
  people: z.number().int(),
  /* The same money split by how it was sold. Two channels rather than the five
     on a person record: everything that is not a practice is somebody who
     bought from us, and that is the distinction the figure is read for.
     optometristUsdCents + directUsdCents === usdCents, always. */
  optometristUsdCents: z.number().int(),
  directUsdCents: z.number().int(),
  optometristPeople: z.number().int(),
  directPeople: z.number().int(),
})

export const revenueSeriesSchema = z.object({
  months: z.array(revenuePointSchema),
  years: z.array(revenuePointSchema),
  /** The last 30 days, one point each, 'YYYY-MM-DD' (UTC). */
  days: z.array(revenuePointSchema).default([]),
})
export type RevenueSeries = z.infer<typeof revenueSeriesSchema>

/* ------------------------------- ages -------------------------------- */

export const ageBandCountSchema = z.object({
  id: z.string(),
  label: z.string(),
  count: z.number().int(),
  /** Share of the people whose age is known — not of everybody. */
  pct: z.number(),
})

export const ageDistributionSchema = z.object({
  /** Everyone the sheet counts, known age or not. */
  total: z.number().int(),
  known: z.number().int(),
  /** No usable date of birth. Reported, never folded into a band. */
  unknown: z.number().int(),
  medianAge: z.number().nullable(),
  bands: z.array(ageBandCountSchema),
})
export type AgeDistribution = z.infer<typeof ageDistributionSchema>

/* ------------------------------ people ------------------------------ */

export const personListItemSchema = z.object({
  /** Campaign or referrer, as a human wrote it. */
  channelDetail: z.string().nullable(),
  /** What we were paid, by whatever route — an order, or a practice's cut. */
  revenueCents: z.number().int().nullable(),
  revenueCurrency: z.string().nullable(),
  /** How it reached us: wix, optometrist, quickbooks, wire…  */
  paymentChannel: z.string().nullable(),
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  stage: stageIdSchema,
  channel: channelSchema,
  practiceName: z.string().nullable(),
  paidAt: z.string().datetime().nullable(),
  sessionsTotal: z.number().int(),
  daysSinceLastSession: z.number().int().nullable(),
  sessionsPerWeek: z.number().nullable(),
  /** Sessions in the Essentials programme: 29, 34, 39 or 49. */
  essentialsSessions: z.number().int().nullable(),
  /** The class it came from. A trailing "?" means the two segments disagreed. */
  essentialsClass: z.string().nullable(),
  /** When the account was created in Oryx — `first_touch_at`, not the Joker row. */
  createdAt: z.string().datetime().nullable(),
  /** True when that date is a Cortex migration timestamp, not a real signup. */
  createdIsMigration: z.boolean(),
  /**
   * Where the created date came from. Oryx never stored a real creation date:
   * the account's own date is trustworthy only for accounts made after the
   * optometrist form shipped, and the first-session date moves forward when a
   * user is teleported. Read the date with this beside it.
   */
  createdSource: z.enum(['account', 'first session', 'migration', 'reset', 'unknown']),
  /** On the hand-kept VIP list. Never inferred from what they paid or trained. */
  isVip: z.boolean(),
  /** An optometrist we work with, not a customer. Kept out of revenue. */
  isOptometrist: z.boolean(),
  /** We sent them a tablet. Stays true once returned — returning is a later state. */
  tabletReceived: z.boolean(),
  tabletReturned: z.boolean(),
  /** When it came back, where anybody wrote it down. Optional by design. */
  tabletReturnedAt: z.string().nullable(),
  /** What this person actually paid, in cents. Test and demo orders excluded. */
  paidCents: z.number().int(),
  paidCurrency: z.string().nullable(),
  /** The numeric id the Oryx dashboard shows. Null for accounts never in Cortex. */
  oryxUserNumber: z.number().int().nullable(),
})
export type PersonListItem = z.infer<typeof personListItemSchema>

/**
 * The sortable columns, named for what the table shows rather than the column
 * it happens to live in. `listPeople` maps each to an expression; anything not
 * in this enum never reaches SQL.
 */
export const peopleSortSchema = z.enum([
  'name',
  'stage',
  'channel',
  'paid',
  'sessions',
  'last',
  'perWeek',
  'program',
  'created',
  'oryxId',
  'paidAmount',
])
export type PeopleSort = z.infer<typeof peopleSortSchema>

/**
 * A manual correction to a person.
 *
 * Every field is optional and `null` is a real value meaning "clear this", so
 * the absence of a key and an explicit null are different instructions —
 * otherwise a form that only edits the channel would wipe the payment date.
 */
export const personEditSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string().max(120).nullable().optional(),
  lastName: z.string().max(120).nullable().optional(),
  paidAt: z.string().datetime().nullable().optional(),
  refundedAt: z.string().datetime().nullable().optional(),
  channel: channelSchema.optional(),
  /** Campaign, referrer or promo code, as a human would write it. */
  channelDetail: z.string().max(200).nullable().optional(),
  /**
   * Who did the eye check. Overrides what the practice implies — somebody
   * attributed to a Bar Ilan row who lives in the US was not examined at Bar
   * Ilan, and only a human can say so. Empty falls back to the practice's own
   * optometrist.
   */
  examinedBy: z.string().max(200).nullable().optional(),
  /**
   * When they first arrived. Editable because the source is often wrong: 151
   * Oryx accounts carry one identical migration timestamp as their creation
   * date, which is when a script ran rather than when anybody signed up.
   */
  firstTouchAt: z.string().datetime().nullable().optional(),
})
export type PersonEdit = z.infer<typeof personEditSchema>

export const peopleQuerySchema = z.object({
  stage: stageIdSchema.optional(),
  channel: channelSchema.optional(),
  search: z.string().max(200).optional(),
  bucketId: z.string().uuid().optional(),
  practiceId: z.string().uuid().optional(),
  /** An age band id from `AGE_BANDS`, or 'unknown' for no date of birth. */
  ageBand: z.string().max(20).optional(),
  /** Only people who have paid, by any route. */
  paidOnly: z.boolean().optional(),
  /** Only the hand-kept VIP list. */
  vipOnly: z.boolean().optional(),
  /** The segment their latest session ran under. -1 means no segment recorded. */
  segment: z.number().int().min(-1).max(99).optional(),
  /** Trained within this window — the Usage "still training" tiles open these. */
  trainedWithin: z.enum(['ever', 'm6', 'm3', 'm1', 'w2', 'w1', 'today', 'yesterday']).optional(),
  /** Paid in this period — 'YYYY-MM' or 'YYYY'. A bar on the revenue chart. */
  paidIn: z.string().regex(/^\d{4}(-\d{2}(-\d{2})?)?$/).optional(),
  /** Where they are with us: a customer has paid and not trained, a patient has trained. */
  lifecycle: z.enum(['lead', 'customer', 'patient', 'refunded']).optional(),
  /* Newest first: the Oryx id climbs with each account, so descending puts the
     most recently created people at the top, which is what anyone opening this
     screen is usually looking for. */
  sort: peopleSortSchema.default('oryxId'),
  dir: z.enum(['asc', 'desc']).default('desc'),
  limit: z.number().int().min(1).max(200).default(50),
  cursor: z.number().int().min(0).default(0),
})

export const peoplePageSchema = z.object({
  total: z.number().int(),
  /** How many of them have paid, by any route. */
  payingTotal: z.number().int(),
  /** Their revenue in dollars — the only unit these can be summed in. */
  revenueUsdTotal: z.number().int(),
  /** How many have trained at least once. */
  trainingTotal: z.number().int(),
  items: z.array(personListItemSchema),
  nextCursor: z.number().int().nullable(),
})

export const timelineEventSchema = z.object({
  at: z.string().datetime(),
  kind: z.enum(['signup', 'order', 'refund', 'account', 'session', 'assessment', 'email', 'stage', 'note']),
  title: z.string(),
  detail: z.string().nullable(),
})

/** An earlier account of the same person, kept for comparison. */
export const earlierEnrolmentSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  oryxUserNumber: z.number().int().nullable(),
  sessionsTotal: z.number().int(),
  supersededAt: z.string().datetime().nullable(),
})
export type EarlierEnrolment = z.infer<typeof earlierEnrolmentSchema>

export const personDetailSchema = personListItemSchema.extend({
  /** The training account's status in Cortex: "Demo" until their details are in, then a real one. */
  accountStatus: z.string().nullable(),
  /** The practice they are under, so the record can reassign them. */
  practiceId: z.string().nullable(),
  /** Who did the eye check, as resolved: override, practice, or Bar Ilan. */
  examinedBy: z.string().nullable(),
  /** How the latest payment arrived, from the order's source. */
  paymentMethod: z.string().nullable(),
  /** Earlier accounts this one replaced. Empty for almost everybody. */
  earlierEnrolments: z.array(earlierEnrolmentSchema).default([]),
  phone: z.string().nullable(),
  ageRange: z.string().nullable(),
  dateOfBirth: z.string().nullable(),
  age: z.number().int().nullable(),
  rx: z.string().nullable(),
  /** The account record's own date. Editable, and for most people invented. */
  signupAt: z.string().datetime().nullable(),
  /** The earliest date we can prove: the account date or the first session. */
  createdAt: z.string().datetime().nullable(),
  createdSource: z.enum(['account', 'first session', 'migration', 'reset', 'unknown']),
  firstSessionAt: z.string().datetime().nullable(),
  lastSessionAt: z.string().datetime().nullable(),
  acuityDeltaLines: z.number().nullable(),
  emailOpens30d: z.number().int(),
  /** True if hidden as a test account, by the name rule or by hand. */
  isTest: z.boolean(),
  /** True only when a human marked them, which is the only mark that can be undone here. */
  testMarked: z.boolean(),
  /** The two stored fields, not the display name — splitting that on a space
      restructures anyone whose first name is two words. */
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  channelDetail: z.string().nullable(),
  /** Staff training on the product. Counted in the population, just labelled. */
  isEmployee: z.boolean(),
  employeeMarked: z.boolean(),
  /** True once a human set the channel, which stops a sync overwriting it. */
  channelLocked: z.boolean(),
  refundedAt: z.string().datetime().nullable(),
  identities: z.array(z.object({ source: z.string(), sourceId: z.string() })),
  timeline: z.array(timelineEventSchema),
})
export type PersonDetail = z.infer<typeof personDetailSchema>

/* ------------------------------ buckets ----------------------------- */

export const bucketSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  kind: z.enum(['dynamic', 'static']),
  ruleText: z.string(),
  goalStage: stageIdSchema.nullable(),
  goalNote: z.string().nullable(),
  memberCount: z.number().int(),
  /** Which screen it opens: the patients list, or prospective users. */
  audience: z.enum(['patients', 'prospects']),
  /** The rule in words, generated from the rule itself. */
  ruleDescription: z.string(),
  /** The rule, for the cohort editor. */
  rule: bucketRuleSchema,
  /** Made on the cohort editor rather than shipped as a starter. */
  editable: z.boolean(),
  /** Housekeeping: its rule asks for hidden people (tests, spam, superseded). Cannot be archived. */
  system: z.boolean(),
})
export type BucketSummary = z.infer<typeof bucketSummarySchema>

export const bucketInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(1000).nullable().default(null),
  audience: z.enum(['patients', 'prospects']).default('patients'),
  rule: bucketRuleSchema,
})
export type BucketInput = z.infer<typeof bucketInputSchema>

export const bucketPreviewSchema = z.object({
  count: z.number().int(),
  description: z.string(),
  sample: z.array(z.object({ id: z.string(), name: z.string() })),
})
export type BucketPreview = z.infer<typeof bucketPreviewSchema>

const optionCount = z.object({ value: z.string(), count: z.number().int() })
export const cohortOptionsSchema = z.object({
  /** Products by store catalog id, named after their newest order. */
  products: z.array(
    z.object({ id: z.string(), name: z.string(), orders: z.number().int(), firstAt: z.string(), lastAt: z.string() }),
  ),
  signupSources: z.array(optionCount),
  signupPromos: z.array(optionCount),
  codes: z.array(optionCount),
})
export type CohortOptions = z.infer<typeof cohortOptionsSchema>

/* ------------------------------ funnel ------------------------------ */

const stepStateSchema = z.object({
  status: z.string(),
  label: z.string(),
  done: z.boolean(),
  blocked: z.boolean(),
  stuck: z.boolean(),
  since: z.string().nullable(),
})

export const cohortFunnelSchema = z.object({
  total: z.number().int(),
  /** Members not refunded and not ruled ineligible. */
  inFunnel: z.number().int(),
  exits: z.object({ refunded: z.number().int(), notEligible: z.number().int(), refundFailed: z.number().int() }),
  steps: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      track: z.enum(['start', 'kit', 'prescription', 'app']),
      /** Done this step. */
      reached: z.number().int(),
      /** This is the first step they have not done. */
      here: z.number().int(),
      stuck: z.number().int(),
      blocked: z.number().int(),
      statuses: z.array(
        z.object({ status: z.string(), label: z.string(), count: z.number().int(), stuck: z.number().int(), blocked: z.number().int(), done: z.boolean() }),
      ),
    }),
  ),
  grid: z.object({
    kit: z.array(z.object({ id: z.string(), label: z.string() })),
    prescription: z.array(z.object({ id: z.string(), label: z.string() })),
    cells: z.array(z.object({ kit: z.string(), prescription: z.string(), count: z.number().int() })),
  }),
  people: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      email: z.string().nullable(),
      current: z.string().nullable(),
      exit: z.enum(['refunded', 'not_eligible']).nullable(),
      refundFailed: z.boolean(),
      steps: z.record(stepStateSchema),
      grid: z.object({ kit: z.string(), prescription: z.string() }).nullable(),
    }),
  ),
})
export type CohortFunnel = z.infer<typeof cohortFunnelSchema>

/* ---------------------------- data health --------------------------- */

export const sourceHealthSchema = z.object({
  source: z.string(),
  label: z.string(),
  status: z.enum(['ok', 'stale', 'never', 'failing']),
  lastSuccessAt: z.string().datetime().nullable(),
  lastRunAt: z.string().datetime().nullable(),
  rowsLastRun: z.number().int().nullable(),
  note: z.string().nullable(),
  /** Whether this source can be refreshed by hand right now. */
  canRefresh: z.boolean(),
  /** Seconds until the cooldown lifts. Zero when it is not cooling down. */
  cooldownSeconds: z.number().int(),
})

/** The sources a person can refresh from the header. */
export const refreshableSourceSchema = z.enum(['klaviyo', 'wix', 'oryx'])
export type RefreshableSource = z.infer<typeof refreshableSourceSchema>

export type SourceHealth = z.infer<typeof sourceHealthSchema>

export const dataHealthSchema = z.object({
  sources: z.array(sourceHealthSchema),
  /** True when every source above is fresh. */
  allOk: z.boolean(),
  unmatchedBySource: z.array(z.object({ source: z.string(), count: z.number().int() })),
  peopleWithoutEmail: z.number().int(),
  peopleWithoutPhone: z.number().int(),
  paidWithoutChannel: z.number().int(),
  /** People who look like the same human enrolled twice. */
  duplicateCandidates: z.array(
    z.object({
      name: z.string(),
      people: z.array(
        z.object({
          id: z.string(),
          email: z.string().nullable(),
          oryxUserNumber: z.number().int().nullable(),
          createdAt: z.string().datetime().nullable(),
          sessionsTotal: z.number().int(),
          /** The parenthetical Oryx puts on a re-enrolment, e.g. "without glasses". */
          note: z.string().nullable(),
        }),
      ),
    }),
  ),
})
export type DataHealth = z.infer<typeof dataHealthSchema>

/* ---------------------------- ingest jobs --------------------------- */

export const syncRequestSchema = z.object({
  source: z.enum(['klaviyo', 'wix', 'oryx_replica', 'cortex', 'gmail', 'seed']),
  mode: z.enum(['incremental', 'backfill']).default('incremental'),
})
export type SyncRequest = z.infer<typeof syncRequestSchema>

/* ---------------------------- webhook in ---------------------------- */

export const zapierEventSchema = z.object({
  event: z.enum(['waitlist_signup', 'purchase']),
  email: z.string().email(),
  phone: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  ageRange: z.string().nullish(),
  rx: z.string().nullish(),
  country: z.string().nullish(),
  promoCode: z.string().nullish(),
  amount: z.number().nullish(),
  currency: z.string().nullish(),
  orderId: z.string().nullish(),
  occurredAt: z.string().datetime().optional(),
})
export type ZapierEvent = z.infer<typeof zapierEventSchema>

/* ------------------------------ worker jobs ------------------------------ */

/** One run of a worker job, from job_run. docs/engineering/prescription-eligibility.md */
export const jobRunSchema = z.object({
  id: z.string(),
  job: z.string(),
  mode: z.string(),
  status: z.enum(['running', 'succeeded', 'failed']),
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime().nullable(),
  heartbeatAt: z.string().datetime(),
  scanned: z.number().int(),
  succeeded: z.number().int(),
  skipped: z.number().int(),
  failed: z.number().int(),
  /** Work found at the start, for "N of M"; null when the job cannot know. */
  total: z.number().int().nullable(),
  note: z.string().nullable(),
  error: z.string().nullable(),
})
export type JobRun = z.infer<typeof jobRunSchema>

/** The importer's last run per source, shown beside the worker's jobs. */
export const sourceRunSchema = z.object({
  source: z.string(),
  label: z.string(),
  status: z.string(),
  mode: z.string(),
  startedAt: z.string().datetime(),
  finishedAt: z.string().datetime().nullable(),
  rowsFetched: z.number().int(),
  rowsLanded: z.number().int(),
  error: z.string().nullable(),
})
export type SourceRun = z.infer<typeof sourceRunSchema>

export const jobOverviewSchema = z.object({
  job: z.string(),
  label: z.string(),
  lastRun: jobRunSchema.nullable(),
  /** The newest run that found work, when the last one found none. */
  lastWork: jobRunSchema.nullable(),
  /** Over every run ever recorded. */
  totals: z.object({
    runs: z.number().int(),
    scanned: z.number().int(),
    succeeded: z.number().int(),
    skipped: z.number().int(),
    failed: z.number().int(),
  }),
  lastSuccessAt: z.string().datetime().nullable(),
  canRun: z.boolean(),
  cooldownSeconds: z.number().int(),
  /** Null when the job needs no outside service; false when it does and lacks it. */
  configured: z.boolean().nullable(),
})
export type JobOverview = z.infer<typeof jobOverviewSchema>

/* ----------------------------- prescriptions ----------------------------- */

export const GRADES = ['suitable', 'not_suitable', 'refer', 'needs_answers', 'incomplete'] as const
export const gradeSchema = z.enum(GRADES)
export type GradeValue = z.infer<typeof gradeSchema>

/**
 * A scanned prescription. Clinical: shown only on the person's own page and
 * the Jobs screen, never exported. The file itself is fetched from
 * /api/prescriptions/:id/file.
 */
export const prescriptionSchema = z.object({
  id: z.string(),
  source: z.enum(['email', 'upload']),
  mailbox: z.string().nullable(),
  senderEmail: z.string().nullable(),
  /** When the file reached us: the email's date, or the upload's. */
  documentDate: z.string().datetime(),
  /** The exam or issue date printed on the prescription; null when it shows none clearly. */
  prescriptionDate: z.string().nullable(),
  /** Expiry printed on it, when it shows one. */
  expiryDate: z.string().nullable().default(null),
  /** What it says it is for: "Reading glasses", "Computer glasses"… */
  label: z.string().nullable().default(null),
  /** Who wrote it and where, as printed (prompt version 3+). */
  prescriber: z
    .object({
      name: z.string().optional(),
      credentials: z.string().optional(),
      license: z.string().optional(),
      practice: z
        .object({
          name: z.string().optional(),
          address: z.string().optional(),
          phone: z.string().optional(),
          email: z.string().optional(),
          website: z.string().optional(),
        })
        .optional(),
    })
    .nullable()
    .default(null),
  /** Another reading of the very same file (mailed again, forwarded): shown once. */
  duplicateOf: z.string().nullable().default(null),
  filename: z.string().nullable(),
  mimeType: z.string(),
  verdict: z.enum(['prescription', 'not_prescription', 'unreadable', 'failed']),
  /** What the file is when it is not a prescription: receipt, invoice, order_confirmation, shipping_label, … */
  documentKind: z.string().nullable(),
  modelId: z.string().nullable(),
  promptVersion: z.number().int().nullable(),
  inputTokens: z.number().int().nullable(),
  outputTokens: z.number().int().nullable(),
  attempts: z.number().int(),
  error: z.string().nullable(),
  od: z.object({ sph: z.number().nullable(), cyl: z.number().nullable(), axis: z.number().nullable() }),
  os: z.object({ sph: z.number().nullable(), cyl: z.number().nullable(), axis: z.number().nullable() }),
  add: z.number().nullable(),
  pd: z.object({ distance: z.number().nullable(), near: z.number().nullable() }),
  patientName: z.string().nullable(),
  patientDateOfBirth: z.string().nullable(),
  patientEmail: z.string().nullable(),
  personId: z.string().nullable(),
  personName: z.string().nullable(),
  matchMethod: z.string().nullable(),
  grade: gradeSchema.nullable(),
  gradeReasons: z.array(z.object({ id: z.string(), text: z.string() })),
  gradeQuestions: z.array(z.string()),
  gradeVersion: z.number().int().nullable(),
  gradedAt: z.string().datetime().nullable(),
  gradeOverride: gradeSchema.nullable(),
  overrideBy: z.string().nullable(),
  overrideAt: z.string().datetime().nullable(),
  overrideNote: z.string().nullable(),
  createdAt: z.string().datetime(),
})
export type Prescription = z.infer<typeof prescriptionSchema>

export const prescriptionFilterSchema = z.object({
  verdict: prescriptionSchema.shape.verdict.optional(),
  grade: gradeSchema.optional(),
  unmatched: z.boolean().optional(),
  limit: z.number().int().min(1).max(500).default(200),
})
export type PrescriptionFilter = z.infer<typeof prescriptionFilterSchema>
